import type { FunctionComponent } from 'react';
import React, { useCallback, useMemo, useState } from 'react';

import type {
  CardAppearance as SmartCardAppearance,
  CardProps as SmartCardProps,
} from '@atlaskit/smart-card';
import { Analytics } from '@trello/atlassian-analytics';
import { ErrorBoundary } from '@trello/error-boundaries';

import { getSafeUrlType } from './getSafeUrlType';
import type { SmartCardClientResponse } from './SmartCardClient';
import { SmartCardWrapper } from './SmartCardWrapper';
import type { SmartLinkAnalyticsContextType } from './SmartLinkAnalyticsContext';

interface Props {
  url: string;
  appearance?: SmartCardAppearance | null;
  plainLink: () => JSX.Element;
  showFrame?: boolean;
  analyticsContext: SmartLinkAnalyticsContextType;
  handleTrelloLinkClick: (e: MouseEvent, linkElem: HTMLAnchorElement) => void;
  onClick?: () => void;
}

export const SmartLink: FunctionComponent<Props> = ({
  url,
  appearance,
  plainLink,
  showFrame = true,
  analyticsContext,
  handleTrelloLinkClick,
  onClick,
}) => {
  const { source, attributes, containers } = analyticsContext;
  const [shouldRender, setShouldRender] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<
    SmartCardClientResponse | undefined
  >();

  const smartCardProps = useMemo(() => {
    const props: SmartCardProps = {
      appearance: appearance || 'inline',
      inheritDimensions: true,
      showActions: true,
      showAuthTooltip: true,
    };

    // disable inheritDimensions for "embed" Smart Cards
    if (props['appearance'] === 'embed') {
      props.frameStyle = showFrame ? 'show' : 'hide';
      props.inheritDimensions = false;
    }

    return props;
  }, [appearance, showFrame]);

  const accessType = resolvedUrl?.meta?.requestAccess?.accessType;
  const cloudId = resolvedUrl?.meta?.requestAccess?.cloudId;
  const definitionId = resolvedUrl?.meta?.definitionId;
  const urlType = resolvedUrl ? getSafeUrlType(resolvedUrl) : undefined;
  const urlAccess = resolvedUrl?.meta?.access;

  const smartLinkAttAnalytics = useMemo(
    () => ({
      source,
      attributes: {
        component: 'smartLink',
        definitionId,
        accessType,
        cloudId,
        urlType,
        urlAccess,
        ...(attributes && attributes),
        ...(containers && containers),
      },
    }),
    [
      source,
      definitionId,
      accessType,
      cloudId,
      urlType,
      urlAccess,
      attributes,
      containers,
    ],
  );

  const handleLinkResolved = useCallback(
    (resolved: SmartCardClientResponse) => {
      setResolvedUrl(resolved);
      setShouldRender(true);
    },
    [],
  );

  const handleLinkUnresolved = useCallback(() => {
    setResolvedUrl(undefined);
    setShouldRender(false);
  }, []);

  const handleSmartCardClick = useCallback(() => {
    // onClick allows for additional custom behavior from the parent component,
    // and will override the default Analytics event
    if (onClick) {
      onClick();
      return;
    }
    // We are allowing a UGCString type within smartLinkAttAnalytics to get passed
    // into this analytics function because there is redaction logic above.
    Analytics.sendClickedLinkEvent({
      linkName: 'smartLink',
      ...smartLinkAttAnalytics,
    });
  }, [smartLinkAttAnalytics, onClick]);

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-aaaa',
        feature: 'Smart Link',
      }}
      errorHandlerComponent={plainLink}
    >
      <SmartCardWrapper
        url={url}
        plainLink={plainLink}
        smartCardProps={smartCardProps}
        onLinkResolved={handleLinkResolved}
        onLinkUnresolved={handleLinkUnresolved}
        shouldRender={shouldRender}
        onSmartCardClick={handleSmartCardClick}
        handleTrelloLinkClick={handleTrelloLinkClick}
        analyticsContextData={smartLinkAttAnalytics}
      />
    </ErrorBoundary>
  );
};
